import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { CheckCircleIcon, XCircleIcon, ClockIcon, DocumentTextIcon } from '@heroicons/react/24/outline';

const AgentStatusIndicator = ({ status, name, description }) => {
  const getStatusColor = () => {
    switch (status) {
      case 'completed': return 'text-green-700 bg-green-50 border-green-200';
      case 'in-progress': return 'text-blue-700 bg-blue-50 border-blue-200';
      case 'error': return 'text-red-700 bg-red-50 border-red-200';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'completed': return <CheckCircleIcon className="w-5 h-5" />;
      case 'error': return <XCircleIcon className="w-5 h-5" />;
      case 'in-progress': return <ClockIcon className="w-5 h-5 animate-spin" />;
      default: return <div className="w-5 h-5 rounded-full border-2 border-gray-300" />;
    }
  };

  return (
    <div className={`agent-status ${status} ${getStatusColor()}`}>
      {getStatusIcon()}
      <div>
        <div className="font-medium">{name}</div>
        <div className="text-sm opacity-75">{description}</div>
      </div>
    </div>
  );
};

const ContentTypeCard = ({ type, title, description, isSelected, onClick }) => (
  <div
    className={`cursor-pointer p-4 border rounded-lg transition-colors ${
      isSelected ? 'border-gray-200 hover:bg-gray-50' : 'border-gray-200 hover:bg-gray-50'
    }`}
    style={{
      borderColor: isSelected ? '#6CB955' : '#E5E7EB',
      backgroundColor: isSelected ? 'rgba(108, 185, 85, 0.05)' : 'transparent'
    }}
    onMouseEnter={(e) => {
      if (!isSelected) {
        e.target.style.borderColor = '#4A8B87';
      }
    }}
    onMouseLeave={(e) => {
      if (!isSelected) {
        e.target.style.borderColor = '#E5E7EB';
      }
    }}
    onClick={() => onClick(type)}
  >
    <h3 className="font-medium text-gray-900">{title}</h3>
    <p className="text-sm text-gray-600 mt-1">{description}</p>
  </div>
);

export default function GeneratePage() {
  const [auditData, setAuditData] = useState(null);
  const [selectedContentType, setSelectedContentType] = useState('homepage-hero');
  const [segmentWeights, setSegmentWeights] = useState({
    families: 40,
    couples: 40,
    business: 20
  });
  const [customPrompt, setCustomPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [generatedContent, setGeneratedContent] = useState(null);
  const [error, setError] = useState(null);
  const [agentStatus, setAgentStatus] = useState({
    strategy: 'pending',
    content: 'pending',
    quality: 'pending'
  });

  const contentTypes = [
    {
      id: 'homepage-hero',
      title: 'Homepage Hero',
      description: 'Compelling hero section to drive bookings and showcase unique value'
    },
    {
      id: 'local-guide',
      title: 'Local Area Guide',
      description: 'Help guests explore local attractions and experiences'
    },
    {
      id: 'booking-cta',
      title: 'Booking Call-to-Action',
      description: 'Convert interest into bookings with persuasive CTAs'
    },
    {
      id: 'accommodation-descriptions',
      title: 'Room Descriptions',
      description: 'Sell accommodation experiences and amenities'
    },
    {
      id: 'facilities-overview',
      title: 'Facilities Overview',
      description: 'Showcase property amenities and facilities'
    },
    {
      id: 'social-media',
      title: 'Social Media Content',
      description: 'Engaging content for social platforms'
    }
  ];

  useEffect(() => {
    // Load audit data from session storage
    const storedAuditData = sessionStorage.getItem('auditData');
    if (storedAuditData) {
      setAuditData(JSON.parse(storedAuditData));
    }
  }, []);

  const handleGenerate = async (e) => {
    e.preventDefault();
    
    if (!auditData) {
      setError('No audit data available. Please run a website audit first.');
      return;
    }

    setLoading(true);
    setError(null);
    setGeneratedContent(null);
    
    // Reset and start agent progress
    setAgentStatus({
      strategy: 'in-progress',
      content: 'pending',
      quality: 'pending'
    });

    try {
      // Simulate agent progression
      setTimeout(() => {
        setAgentStatus(prev => ({
          ...prev,
          strategy: 'completed',
          content: 'in-progress'
        }));
      }, 2000);

      setTimeout(() => {
        setAgentStatus(prev => ({
          ...prev,
          content: 'completed',
          quality: 'in-progress'
        }));
      }, 5000);

      const response = await axios.post('/api/generate', {
        audit_data: auditData,
        content_type: selectedContentType,
        segment_weights: segmentWeights,
        custom_prompt: customPrompt || undefined
      });

      setGeneratedContent(response.data);
      setAgentStatus({
        strategy: 'completed',
        content: 'completed',
        quality: 'completed'
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate content');
      setAgentStatus(prev => ({
        ...prev,
        strategy: prev.strategy === 'in-progress' ? 'error' : prev.strategy,
        content: prev.content === 'in-progress' ? 'error' : prev.content,
        quality: prev.quality === 'in-progress' ? 'error' : prev.quality
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleSegmentChange = (segment, value) => {
    const newWeights = { ...segmentWeights, [segment]: parseInt(value) };
    setSegmentWeights(newWeights);
  };

  const handleClearData = () => {
    // Clear session storage
    sessionStorage.removeItem('auditData');
    // Reset all state
    setAuditData(null);
    setGeneratedContent(null);
    setError(null);
    setAgentStatus({
      strategy: 'pending',
      content: 'pending',
      quality: 'pending'
    });
    // Reset form to defaults
    setSelectedContentType('homepage-hero');
    setSegmentWeights({
      families: 40,
      couples: 40,
      business: 20
    });
    setCustomPrompt('');
  };

  return (
    <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
      <div className="px-4 py-6 sm:px-0">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Content Generator</h1>
          {auditData && (
            <button
              onClick={handleClearData}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors"
              style={{ 
                focusRingColor: '#6CB955',
                borderColor: '#4A8B87'
              }}
              onMouseEnter={(e) => {
                e.target.style.borderColor = '#2C5F5D';
                e.target.style.color = '#2C5F5D';
              }}
              onMouseLeave={(e) => {
                e.target.style.borderColor = '#4A8B87';
                e.target.style.color = '#374151';
              }}
            >
              Clear & Start New Audit
            </button>
          )}
        </div>
        
        {!auditData && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-6">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">No Audit Data</h3>
                <div className="mt-2 text-sm text-yellow-700">
                  Please run a website audit first to generate targeted content.
                  <a href="/audit" className="font-medium underline ml-1">Go to Audit Page</a>
                </div>
              </div>
            </div>
          </div>
        )}

        {auditData && (
          <div className="space-y-6">
            {/* Property Summary */}
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Property Summary</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-500">Brand:</span>
                  <span className="ml-2 text-gray-900">{auditData.brandName}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-500">Location:</span>
                  <span className="ml-2 text-gray-900">{auditData.location}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-500">Type:</span>
                  <span className="ml-2 text-gray-900 capitalize">{auditData.propertyType?.replace('_', ' ')}</span>
                </div>
              </div>
            </div>

            {/* Content Type Selection */}
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Select Content Type</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {contentTypes.map((type) => (
                  <ContentTypeCard
                    key={type.id}
                    type={type.id}
                    title={type.title}
                    description={type.description}
                    isSelected={selectedContentType === type.id}
                    onClick={setSelectedContentType}
                  />
                ))}
              </div>
            </div>

            {/* Target Audience Weights */}
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Target Audience Balance</h2>
              <div className="space-y-4">
                {Object.entries(segmentWeights).map(([segment, weight]) => (
                  <div key={segment}>
                    <label className="block text-sm font-medium text-gray-700 capitalize mb-2">
                      {segment} ({weight}%)
                    </label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={weight}
                      onChange={(e) => handleSegmentChange(segment, e.target.value)}
                      className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Custom Prompt */}
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Custom Instructions (Optional)</h2>
              <textarea
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none transition-colors"
                style={{
                  focusRingColor: '#6CB955',
                  focusBorderColor: '#6CB955'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#6CB955';
                  e.target.style.outline = '2px solid #6CB955';
                  e.target.style.outlineOffset = '2px';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#D1D5DB';
                  e.target.style.outline = 'none';
                }}
                placeholder="Add any specific requirements or focus areas for the content..."
              />
            </div>

            {/* Generate Button */}
            <div className="bg-white shadow rounded-lg p-6">
              <button
                onClick={handleGenerate}
                disabled={loading}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2"
                style={{ 
                  backgroundColor: '#6CB955',
                  focusRingColor: '#4A9B3A'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#4A9B3A'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#6CB955'}
              >
                {loading ? 'Generating Content...' : 'Generate Content'}
              </button>
            </div>
          </div>
        )}

        {/* Agent Status */}
        {(loading || generatedContent || error) && (
          <div className="bg-white shadow rounded-lg p-6 mt-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">AI Agent Progress</h2>
            <div className="space-y-3">
              <AgentStatusIndicator
                status={agentStatus.strategy}
                name="Strategy Agent"
                description="Analysing market positioning and developing content strategy"
              />
              <AgentStatusIndicator
                status={agentStatus.content}
                name="Content Agent"
                description="Creating compelling, conversion-focused content"
              />
              <AgentStatusIndicator
                status={agentStatus.quality}
                name="Quality Agent"
                description="Ensuring SEO optimisation and content quality standards"
              />
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mt-6">
            <div className="flex">
              <XCircleIcon className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Generation Failed</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>
        )}

        {/* Generated Content */}
        {generatedContent && (
          <div className="bg-white shadow rounded-lg p-6 mt-6">
            <div className="flex items-center mb-6">
              <DocumentTextIcon className="h-6 w-6 mr-2" style={{ color: '#6CB955' }} />
              <h2 className="text-lg font-medium text-gray-900">Generated Content</h2>
            </div>
            
            {/* Content Metrics */}
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
              <div className="text-center">
                <div className="text-2xl font-bold" style={{ color: '#6CB955' }}>{generatedContent.word_count}</div>
                <div className="text-sm text-gray-500">Words</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{generatedContent.seo_score}</div>
                <div className="text-sm text-gray-500">SEO Score</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{generatedContent.readability_score}</div>
                <div className="text-sm text-gray-500">Readability</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">{generatedContent.content_type}</div>
                <div className="text-sm text-gray-500">Type</div>
              </div>
            </div>

            {/* Content Preview */}
            <div className="content-preview mb-6">
              <div className="bg-white border rounded-lg p-4 mb-4">
                <h4 className="font-medium text-gray-900 mb-2">Generated Content</h4>
                <div className="prose prose-sm max-w-none">
                  <pre className="whitespace-pre-wrap text-sm text-gray-700 font-normal">
                    {generatedContent.content}
                  </pre>
                </div>
              </div>
              
              {/* Strategy Analysis */}
              {generatedContent.strategy_analysis && generatedContent.strategy_analysis !== "Strategy completed" && (
                <div className="bg-blue-50 border rounded-lg p-4 mb-4">
                  <h4 className="font-medium text-blue-900 mb-2">Strategy Agent Analysis</h4>
                  <div className="text-sm text-blue-700">
                    <pre className="whitespace-pre-wrap font-normal">{generatedContent.strategy_analysis}</pre>
                  </div>
                </div>
              )}
              
              {/* Quality Analysis */}
              {generatedContent.quality_analysis && generatedContent.quality_analysis !== "Quality analysis completed" && (
                <div className="bg-green-50 border rounded-lg p-4 mb-4">
                  <h4 className="font-medium text-green-900 mb-2">Quality Agent Analysis</h4>
                  <div className="text-sm text-green-700">
                    <pre className="whitespace-pre-wrap font-normal">{generatedContent.quality_analysis}</pre>
                  </div>
                </div>
              )}
            </div>

            {/* Keywords */}
            {generatedContent.keywords && generatedContent.keywords.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">Keywords</h3>
                <div className="flex flex-wrap gap-2">
                  {generatedContent.keywords.map((keyword, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium"
                      style={{
                        backgroundColor: 'rgba(108, 185, 85, 0.1)',
                        color: '#2C5F5D'
                      }}
                    >
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Recommendations */}
            {generatedContent.recommendations && generatedContent.recommendations.length > 0 && (
              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">Recommendations</h3>
                <ul className="space-y-2">
                  {generatedContent.recommendations.map((rec, index) => (
                    <li key={index} className="text-sm text-gray-700 flex items-start">
                      <span className="inline-block w-1.5 h-1.5 rounded-full mt-2 mr-3 flex-shrink-0" style={{ backgroundColor: '#6CB955' }}></span>
                      {rec}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex space-x-4 mt-6 pt-6 border-t border-gray-200">
              <button
                onClick={() => navigator.clipboard.writeText(generatedContent.content)}
                className="flex-1 text-white px-4 py-2 rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors"
                style={{
                  backgroundColor: '#6CB955',
                  focusRingColor: '#4A9B3A'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#4A9B3A'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#6CB955'}
              >
                Copy Content
              </button>
              <button
                onClick={() => window.print()}
                className="flex-1 bg-gray-600 text-white px-4 py-2 rounded-md font-medium hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              >
                Print/Save as PDF
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}